<template>
  <div class="page-container">
    <div class="page-header">
      <div class="page-title">
        {{ mode === 'edit' ? '대리점 상세' : '대리점 등록' }}
        <span class="count-text">{{ form.name || '-' }}</span>
      </div>

      <div class="header-buttons">
        <button class="btn ghost" @click="$emit('back')">목록으로</button>
        <button class="btn primary" @click="saveAgency">저장</button>
      </div>
    </div>

    <div class="content-wrapper">
      <!-- 기본 정보 -->
      <div class="detail-card">
        <div class="form-row">
          <label class="form-label">대리점명</label>
          <input v-model="form.name" class="form-input" type="text" placeholder="대리점명을 입력하세요" />
        </div>

        <div class="form-row">
          <label class="form-label">담당자명</label>
          <input v-model="form.managerName" class="form-input" type="text" placeholder="담당자명을 입력하세요" />
        </div>

        <div class="form-row">
          <label class="form-label">연락처</label>
          <input v-model="form.managerPhone" class="form-input" type="text" @input="onlyNumber" />
        </div>

        <div class="form-row">
          <label class="form-label">주소</label>
          <div class="address-field">
            <input v-model="form.address" class="form-input" type="text" placeholder="주소 검색을 진행해주세요" readonly />
            <button class="btn secondary sm" type="button" @click="searchAddress">
              주소 검색
            </button>
          </div>
        </div>

        <div class="form-row">
          <label class="form-label">상세주소</label>
          <input
            ref="addressDetailInput"
            v-model="form.addressDetail"
            class="form-input"
            type="text"
            placeholder="상세주소를 입력해주세요"
          />
        </div>

        <div v-if="extraAddress" class="form-row">
          <label class="form-label">참고항목</label>
          <input :value="extraAddress" class="form-input" type="text" readonly />
        </div>

        <div class="form-row">
          <label class="form-label">설명</label>
          <textarea v-model="form.explain" class="form-textarea" placeholder="설명을 입력하세요"></textarea>
        </div>
      </div>

      <!-- 연동 현장 -->
      <div v-if="mode === 'edit'" class="detail-card">
        <div class="panel-title-row">
          <div class="panel-title">연동 현장 목록</div>
          <div class="section-count">총 {{ linkedSites.length }} 건</div>
        </div>

        <div class="table-section">
          <table v-if="linkedSites.length" class="data-table linked-site-table">
            <thead>
              <tr>
                <th>공사명</th>
                <th>주소</th>
                <th>상태</th>
              </tr>
            </thead>

            <tbody>
              <tr v-for="site in linkedSites" :key="site.id">
                <td class="td-wrap" :title="site.siteName || '-'">
                  {{ site.siteName || '-' }}
                </td>

                <td class="td-wrap td-address" :title="fullSiteAddress(site)">
                  {{ fullSiteAddress(site) || '-' }}
                </td>

                <td>{{ site.status || '-' }}</td>
              </tr>
            </tbody>
          </table>

          <div v-else class="table-empty">
            연동된 현장이 없습니다.
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import api from '@/api/axios'

export default {
  name: 'AgencyDetail',

  props: {
    agencyId: {
      type: Number,
      default: null,
    },
    mode: {
      type: String,
      default: 'create',
    },
  },

  data() {
    return {
      loading: false,
      isPostcodeScriptLoaded: false,
      extraAddress: '',
      zonecode: '',
      linkedSites: [],
      form: {
        id: null,
        name: '',
        managerName: '',
        managerPhone: '',
        address: '',
        addressDetail: '',
        explain: '',
      },
    }
  },

  mounted() {
    this.loadPostcodeScript()

    if (this.mode === 'edit' && this.agencyId) {
      this.fetchDetail()
      this.fetchLinkedSites()
    }
  },

  watch: {
    agencyId(newVal, oldVal) {
      if (this.mode === 'edit' && newVal && newVal !== oldVal) {
        this.fetchDetail()
        this.fetchLinkedSites()
      }
    },
  },

  methods: {
    async fetchDetail() {
      this.loading = true

      try {
        const response = await api.get('/app/agency/detail', {
          params: { id: this.agencyId },
        })

        const result = response.data
        const detail = result?.result

        this.form = {
          id: detail?.id ?? null,
          name: detail?.name ?? '',
          managerName: detail?.managerName ?? '',
          managerPhone: detail?.managerPhone ?? '',
          address: detail?.address ?? '',
          addressDetail: detail?.addressDetail ?? '',
          explain: detail?.explain ?? '',
        }
      } catch (error) {
        console.error('대리점 상세 조회 실패', error)
      } finally {
        this.loading = false
      }
    },

    async fetchLinkedSites() {
      if (!this.agencyId) {
        this.linkedSites = []
        return
      }

      try {
        const response = await api.get('/app/agency/sites', {
          params: { agencyId: this.agencyId },
        })

        const result = response.data
        this.linkedSites = Array.isArray(result?.result) ? result.result : []
      } catch (error) {
        console.error('대리점 연동 현장 목록 조회 실패', error)
        this.linkedSites = []
      }
    },

    async saveAgency() {
      if (!this.form.name?.trim()) {
        alert('대리점명을 입력해주세요.')
        return
      }

      if (!this.form.address?.trim()) {
        alert('주소를 입력해주세요.')
        return
      }

      try {
        if (this.mode === 'edit' && this.form.id) {
          await api.post('/app/agency/update', {
            id: this.form.id,
            name: this.form.name,
            managerName: this.form.managerName,
            managerPhone: this.form.managerPhone,
            address: this.form.address,
            addressDetail: this.form.addressDetail,
            explain: this.form.explain,
          })
        } else {
          await api.post('/app/agency/create', {
            name: this.form.name,
            managerName: this.form.managerName,
            managerPhone: this.form.managerPhone,
            address: this.form.address,
            addressDetail: this.form.addressDetail,
            explain: this.form.explain,
          })
        }

        alert('저장되었습니다.')
        this.$emit('saved')
      } catch (error) {
        console.error('대리점 저장 실패', error)
        alert('저장에 실패했습니다.')
      }
    },

    fullSiteAddress(site) {
      const address = site?.address || ''
      const addressDetail = site?.addressDetail || ''
      return [address, addressDetail].filter(Boolean).join(' ')
    },

    loadPostcodeScript() {
      if (window.daum && window.daum.Postcode) {
        this.isPostcodeScriptLoaded = true
        return
      }

      const script = document.createElement('script')
      script.src = '//t1.daumcdn.net/mapjsapi/bundle/postcode/prod/postcode.v2.js'
      script.async = true
      script.onload = () => {
        this.isPostcodeScriptLoaded = true
      }

      document.head.appendChild(script)
    },

    searchAddress() {
      if (!(window.daum && window.daum.Postcode)) {
        alert('주소 검색 스크립트가 아직 로드되지 않았습니다.')
        return
      }

      new window.daum.Postcode({
        oncomplete: (data) => {
          let addr = ''
          let extraAddr = ''

          addr = data.userSelectedType === 'R' ? data.roadAddress : data.jibunAddress

          this.form.address = addr
          this.extraAddress = extraAddr

          this.$nextTick(() => {
            this.$refs.addressDetailInput?.focus()
          })
        },
      }).open()
    },

    onlyNumber(e) {
      this.form.managerPhone = e.target.value.replace(/[^0-9]/g, '')
    },
  },
}
</script>

<style scoped>
@import "@/styles/admin/admin-layout.css";
@import "@/styles/admin/admin-button.css";
@import "@/styles/admin/admin-input.css";
@import "@/styles/admin/admin-form.css";
@import "@/styles/admin/admin-card.css";
@import "@/styles/admin/admin-table.css";

/* =========================
   TABLE ROW ACTIVE
========================= */

/* =========================
   LINKED SITE TABLE
========================= */

.linked-site-table {
  table-layout: fixed;
  width: 100%;
}

.linked-site-table th:nth-child(1),
.linked-site-table td:nth-child(1) {
  width: 36%;
}

.linked-site-table th:nth-child(2),
.linked-site-table td:nth-child(2) {
  width: 48%;
}

.linked-site-table th:nth-child(3),
.linked-site-table td:nth-child(3) {
  width: 16%;
}

.td-wrap {
  white-space: normal !important;
  overflow: visible !important;
  text-overflow: clip !important;
  word-break: keep-all;
  overflow-wrap: anywhere;
  line-height: 1.5;
}

.td-address {
  text-align: left;
}

/* =========================
   TABLE ROW ACTIVE
========================= */

.data-table tbody tr {
  transition:
    background-color 0.15s ease,
    box-shadow 0.15s ease;
}

.data-table tbody tr:hover td {
  background: #f8fafc;
}

.data-table tbody tr:hover {
  box-shadow: inset 3px 0 0 #e58a13;
}
</style>